<template>
  <div class="card">
    <div class="card-header d-flex justify-content-between align-items-center flex-wrap">
      <h1 class="card-title mb-0">
        Client Engagement
      </h1>
      <div style="min-width: 240px;">
        <input
          v-model="search"
          type="text"
          class="form-control form-control-sm"
          placeholder="Search name, email, company…"
        >
      </div>
    </div>

    <div class="card-body">
      <div
        v-if="loading"
        class="text-center py-5"
      >
        <b-spinner
          variant="primary"
          label="Loading"
        />
      </div>

      <template v-else>
        <div class="table-responsive">
          <table class="table table-striped table-hover table-vcenter">
            <thead>
              <tr>
                <th style="width: 50px;">
                  #
                </th>
                <th>
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0"
                    @click="toggleSort('name')"
                  >
                    Name
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0"
                    @click="toggleSort('email')"
                  >
                    Email
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0"
                    @click="toggleSort('companies')"
                  >
                    Company(ies)
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0"
                    @click="toggleSort('engagements_count')"
                  >
                    Engagements (paid/completed)
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0"
                    @click="toggleSort('last_activity')"
                  >
                    Last activity
                  </button>
                </th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in (pagination ? pagination.data : [])"
                :key="row.client_id"
              >
                <td>{{ pagination.from + index }}</td>
                <td>{{ fullName(row) }}</td>
                <td>{{ row.email }}</td>
                <td>{{ row.companies || '—' }}</td>
                <td>{{ row.engagements_count }}</td>
                <td>{{ formatDate(row.last_activity) }}</td>
                <td>
                  <button
                    type="button"
                    class="btn btn-sm btn-primary"
                    :disabled="drafting && activeClientId === row.client_id"
                    @click="openDraft(row)"
                  >
                    <b-spinner
                      v-if="drafting && activeClientId === row.client_id"
                      small
                      class="mr-1"
                    />
                    Draft outreach email
                  </button>
                </td>
              </tr>
              <tr v-if="pagination && pagination.data.length === 0">
                <td
                  colspan="7"
                  class="text-center text-muted"
                >
                  No clients found.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <vue-pagination
          v-if="pagination && pagination.last_page > 1"
          :data="pagination"
          @pagination-change-page="fetchRows"
        />
      </template>
    </div>

    <b-modal
      v-model="modalOpen"
      title="Outreach email draft"
      size="lg"
      ok-title="Copy"
      cancel-title="Close"
      @ok="copyDraft"
    >
      <div
        v-if="draftError"
        class="alert alert-danger"
      >
        {{ draftError }}
      </div>

      <div class="form-group">
        <label for="draft-subject">Subject</label>
        <input
          id="draft-subject"
          v-model="draftSubject"
          type="text"
          class="form-control"
          :disabled="drafting"
        >
      </div>

      <div class="form-group">
        <label for="draft-body">Body</label>
        <textarea
          id="draft-body"
          v-model="draftBody"
          class="form-control"
          rows="10"
          :disabled="drafting"
        />
      </div>

      <div
        v-if="drafting"
        class="text-muted small mb-2"
      >
        <b-spinner
          small
          class="mr-1"
        />
        Generating draft…
      </div>
      <div
        v-if="copyFeedback"
        class="text-success small mb-2"
      >
        {{ copyFeedback }}
      </div>

      <template #modal-footer="{ ok, cancel }">
        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="drafting"
          @click="regenerate"
        >
          <b-spinner
            v-if="drafting"
            small
            class="mr-1"
          />
          Regenerate
        </button>
        <button
          type="button"
          class="btn btn-secondary"
          @click="cancel()"
        >
          Close
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="drafting || !canCopy"
          @click="ok()"
        >
          Copy
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment-timezone";

export default {
    name: "ClientEngagementDashboard",
    props: {
        apiUrl: {
            type: String,
            required: true
        },
        draftUrlTemplate: {
            type: String,
            required: true
        },
        csrfToken: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            pagination: null,
            loading: false,
            search: "",
            sortKey: "engagements_count",
            sortDir: "desc",
            modalOpen: false,
            drafting: false,
            activeClientId: null,
            draftRequestSeq: 0,
            draftSubject: "",
            draftBody: "",
            draftError: "",
            copyFeedback: ""
        };
    },
    computed: {
        canCopy() {
            return !!(this.draftSubject || this.draftBody);
        }
    },
    watch: {
        search: _.debounce(function () {
            this.fetchRows(1);
        }, 300)
    },
    mounted() {
        this.fetchRows();
    },
    methods: {
        fetchRows(page = 1) {
            this.loading = true;

            axios.get(this.apiUrl, {
                params: {
                    search: this.search,
                    sort_key: this.sortKey,
                    sort_dir: this.sortDir,
                    page
                }
            })
                .then(({ data }) => {
                    this.pagination = data;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        fullName(row) {
            return [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
        },
        formatDate(value) {
            if (!value) return "—";
            const m = moment(value);
            return m.isValid() ? m.format("YYYY-MM-DD") : "—";
        },
        toggleSort(key) {
            if (this.sortKey === key) {
                this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
            } else {
                this.sortKey = key;
                this.sortDir = key === "engagements_count" || key === "distinct_experts" || key === "last_activity"
                    ? "desc"
                    : "asc";
            }
            this.fetchRows(1);
        },
        async openDraft(row) {
            this.activeClientId = row.client_id;
            this.modalOpen = true;
            await this.fetchDraft(row.client_id);
        },
        async regenerate(bvEvent) {
            if (bvEvent && bvEvent.preventDefault) {
                bvEvent.preventDefault();
            }
            if (!this.activeClientId || this.drafting) return;
            await this.fetchDraft(this.activeClientId);
        },
        isCurrentDraftRequest(requestId, clientId) {
            return requestId === this.draftRequestSeq && this.activeClientId === clientId;
        },
        async fetchDraft(clientId) {
            const requestId = ++this.draftRequestSeq;
            this.drafting = true;
            this.draftSubject = "";
            this.draftBody = "";
            this.draftError = "";
            this.copyFeedback = "";

            try {
                const url = this.draftUrlTemplate.replace("__ID__", clientId);
                const result = await axios.post(url, {}, {
                    headers: {
                        "X-CSRF-TOKEN": this.csrfToken
                    }
                });

                if (!this.isCurrentDraftRequest(requestId, clientId)) {
                    return;
                }

                this.draftSubject = result.data.subject || "";
                this.draftBody = result.data.body || "";
            } catch (error) {
                if (!this.isCurrentDraftRequest(requestId, clientId)) {
                    return;
                }

                const message = error.response && error.response.data && error.response.data.error
                    ? error.response.data.error
                    : "Failed to draft email.";
                this.draftError = message;
                this.draftSubject = "";
                this.draftBody = "";
            } finally {
                if (requestId === this.draftRequestSeq) {
                    this.drafting = false;
                }
            }
        },
        async copyDraft(bvEvent) {
            if (bvEvent && bvEvent.preventDefault) {
                bvEvent.preventDefault();
            }
            if (this.drafting || !this.canCopy) {
                return;
            }
            const text = `${this.draftSubject}\n\n${this.draftBody}`;
            try {
                await navigator.clipboard.writeText(text);
                this.copyFeedback = "Copied to clipboard.";
            } catch (e) {
                this.draftError = "Could not copy to clipboard.";
            }
        }
    }
};
</script>
